import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    DialogContent,
    Grid,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import { DatePicker } from "../MoleDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import RefreshButton from "../RefreshButton";
import { createFilterOptions, Util } from "../../util/Util";
import { useAppSocket } from "../../providers/AppSocketContext";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { INVENTORY_TYPES, MIN_DATE } from "../../util/constants";
import { useTranslation } from "react-i18next";
import { FilterListOutlined, FileDownloadOutlined } from "@mui/icons-material";
import PageOrientationCards, { PageOrientation } from "../PageOrientationCards";
import {
    MoleCancelButton,
    MoleDialog,
    MoleDialogActions,
    MoleDialogHeader,
    MolePrimaryButton,
    useMoleThemeColor,
} from "../MoleDialogShell";

export type FilterFieldType =
    | "date"
    | "date-range"
    | "client"
    | "clients"
    | "agent"
    | "agents"
    | "suppliers"
    | "depot"
    | "inventory-type"
    | "orientation";

export type ListSerializationFormat = 'array' | 'csv';

export interface ReportFilterFieldConfig {
    type: FilterFieldType;
    required?: boolean;
    /** How to serialize multi-select values in the built payload. Defaults to 'array'. */
    listFormat?: ListSerializationFormat;
}

/**
 * Serialize a list of IDs according to the chosen format.
 *
 * - 'array' (default): pass through as-is — axios will repeat the param key
 * - 'csv': join as a comma-separated string
 */
export const serializeList = (
    ids: number[],
    format: ListSerializationFormat = 'array'
): number[] | string => {
    if (format === 'csv') return ids.join(',');
    return ids;
};

export interface DynamicFilterValues {
    date: Date | null;
    dateRange: {
        from: Date | null;
        to: Date | null;
    };
    clientId: number | null;
    clientIds: number[];
    agentId: number | null;
    agentIds: number[];
    supplierIds: number[];
    depotId: number | null;
    inventoryType: string | null;
    orientation: PageOrientation;
}

interface DynamicReportFilterDialogProps {
    open: boolean;
    title?: string;
    fields: ReportFilterFieldConfig[];
    allowEmptyFilters?: boolean;
    onClose: () => void;
    onApply: (values: DynamicFilterValues) => void;
}

interface AccountOption {
    id: number;
    acc_name: string;
    is_agent?: boolean;
}

interface DepotOption {
    id: number;
    DepotName: string;
}

interface SupplierOption {
    id: number;
    sup_name: string;
}

const hasField = (fields: ReportFilterFieldConfig[], type: FilterFieldType): boolean => {
    return fields.some((field) => field.type === type);
};

const hasAnySelection = (values: DynamicFilterValues): boolean => {
    if (values.date) return true;
    if (values.dateRange.from || values.dateRange.to) return true;
    if (values.clientId) return true;
    if (values.clientIds.length > 0) return true;
    if (values.agentId) return true;
    if (values.agentIds.length > 0) return true;
    if (values.supplierIds.length > 0) return true;
    if (values.depotId) return true;
    if (values.inventoryType) return true;
    return false;
};

const DynamicReportFilterDialog = ({
    open,
    title,
    fields,
    allowEmptyFilters = false,
    onClose,
    onApply,
}: DynamicReportFilterDialogProps) => {
    const { t } = useTranslation();
    const getInventoryTypeLabel = (item: { key: string; label: string }) =>
        t(`common:inventory.types.${item.key}`, { defaultValue: item.label });
    const { appSettings } = useAppSocket();
    const themeColor = useMoleThemeColor();
    const { api } = useAuth();
    const { queueReRun } = useUI();

    const [accounts, setAccounts] = useState<AccountOption[]>([]);
    const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
    const [depots, setDepots] = useState<DepotOption[]>([]);

    const [isAccountsLoading, setIsAccountsLoading] = useState(false);
    const [isSuppliersLoading, setIsSuppliersLoading] = useState(false);
    const [isDepotsLoading, setIsDepotsLoading] = useState(false);

    const [errorInput, setErrorInput] = useState("");
    const [generalError, setGeneralError] = useState<string | null>(null);
    const [dateMode, setDateMode] = useState<"date" | "range">("range");

    const [values, setValues] = useState<DynamicFilterValues>({
        date: null,
        dateRange: {
            from: null,
            to: null,
        },
        clientId: null,
        clientIds: [],
        agentId: null,
        agentIds: [],
        supplierIds: [],
        depotId: null,
        inventoryType: null,
        orientation: "landscape",
    });

    const minDate = appSettings?.SYSTEM_INCEPTION_DATE?.value || MIN_DATE;

    const clientOptions = useMemo(() => accounts, [accounts]);
    const agentOptions = useMemo(() => accounts.filter((acc) => acc.is_agent), [accounts]);

    const shouldShowDateModeSwitch = hasField(fields, "date") && hasField(fields, "date-range");

    const fetchAccounts = async () => {
        setIsAccountsLoading(true);
        const res = await api.get("/clients", {
            params: {
                Active: true,
                Type: "CONSUMER",
            },
        });

        if (res) {
            if (res.status === 200) {
                res.data.sort((a: AccountOption, b: AccountOption) => a.acc_name.localeCompare(b.acc_name));
                setAccounts(res.data);
            } else if (res.status === 401 || res.status === 422) {
                queueReRun(fetchAccounts);
            } else {
                console.error("Failed to fetch clients/agents:", res);
            }
        }

        setIsAccountsLoading(false);
    };

    const fetchSuppliers = async () => {
        setIsSuppliersLoading(true);
        const res = await api.get("/supplier");

        if (res) {
            if (res.status === 200) {
                res.data.sort((a: SupplierOption, b: SupplierOption) =>
                    a.sup_name.localeCompare(b.sup_name),
                );
                setSuppliers(res.data);
            } else if (res.status === 401 || res.status === 422) {
                queueReRun(fetchSuppliers);
            } else {
                console.error("Failed to fetch suppliers:", res);
            }
        }

        setIsSuppliersLoading(false);
    };

    const fetchDepots = async () => {
        setIsDepotsLoading(true);
        const res = await api.get("/depot/meterred",
            {}
        );

        if (res) {
            if (res.status === 200) {
                setDepots(res.data);
            } else if (res.status === 401 || res.status === 422) {
                queueReRun(fetchDepots);
            } else {
                console.error("Failed to fetch depots:", res);
            }
        }

        setIsDepotsLoading(false);
    };

    useEffect(() => {
        if (!open) return;

        const needsAccounts =
            hasField(fields, "client") ||
            hasField(fields, "clients") ||
            hasField(fields, "agent") ||
            hasField(fields, "agents");

        if (needsAccounts && accounts.length === 0 && !isAccountsLoading) {
            fetchAccounts();
        }

        if (hasField(fields, "suppliers") && suppliers.length === 0 && !isSuppliersLoading) {
            fetchSuppliers();
        }

        if (hasField(fields, "depot") && depots.length === 0 && !isDepotsLoading) {
            fetchDepots();
        }

        if (hasField(fields, "date") && !hasField(fields, "date-range")) {
            setDateMode("date");
        }
        if (!hasField(fields, "date") && hasField(fields, "date-range")) {
            setDateMode("range");
        }
        setValues((prev) => ({ ...prev, orientation: "landscape" }));
    }, [open]);

    const setDateError = (inputId: string) => {
        setErrorInput(inputId);
        setTimeout(() => setErrorInput(""), 1500);
    };

    const validateRequiredFields = (): boolean => {
        const requiredTypes = fields.filter((field) => field.required).map((field) => field.type);

        for (const requiredType of requiredTypes) {
            if (requiredType === "date") {
                const hasDate = dateMode === "date" && !!values.date;
                if (!hasDate) {
                    setGeneralError(t("common:validation.allFieldsRequired"));
                    return false;
                }
            }

            if (requiredType === "date-range") {
                const hasRange =
                    dateMode === "range" &&
                    !!values.dateRange.from &&
                    !!values.dateRange.to;
                if (!hasRange) {
                    setGeneralError(t("common:validation.allFieldsRequired"));
                    return false;
                }
            }

            if (requiredType === "client" && !values.clientId) {
                setGeneralError(t("common:validation.allFieldsRequired"));
                return false;
            }

            if (requiredType === "clients" && values.clientIds.length === 0) {
                setGeneralError(t("common:validation.allFieldsRequired"));
                return false;
            }

            if (requiredType === "agent" && !values.agentId) {
                setGeneralError(t("common:validation.allFieldsRequired"));
                return false;
            }

            if (requiredType === "agents" && values.agentIds.length === 0) {
                setGeneralError(t("common:validation.allFieldsRequired"));
                return false;
            }

            if (requiredType === "suppliers" && values.supplierIds.length === 0) {
                setGeneralError(t("common:validation.allFieldsRequired"));
                return false;
            }

            if (requiredType === "depot" && !values.depotId) {
                setGeneralError(t("common:validation.allFieldsRequired"));
                return false;
            }

            if (requiredType === "inventory-type" && !values.inventoryType) {
                setGeneralError(t("common:validation.allFieldsRequired"));
                return false;
            }
        }

        if (
            dateMode === "range" &&
            values.dateRange.from &&
            values.dateRange.to &&
            values.dateRange.from > values.dateRange.to
        ) {
            setGeneralError(t("common:validation.invalidDate"));
            return false;
        }

        if (!allowEmptyFilters && !hasAnySelection(values)) {
            setGeneralError(t("common:validation.allFieldsRequired"));
            return false;
        }

        setGeneralError(null);
        return true;
    };

    const handleApply = () => {
        const normalizedValues: DynamicFilterValues = {
            ...values,
            date: dateMode === "date" ? values.date : null,
            dateRange:
                dateMode === "range"
                    ? values.dateRange
                    : {
                        from: null,
                        to: null,
                    },
        };

        if (!validateRequiredFields()) {
            return;
        }

        onApply(normalizedValues);
        onClose();
    };

    const resetFilters = () => {
        setGeneralError(null);
        setValues({
            date: null,
            dateRange: {
                from: null,
                to: null,
            },
            clientId: null,
            clientIds: [],
            agentId: null,
            agentIds: [],
            supplierIds: [],
            depotId: null,
            inventoryType: null,
            orientation: "landscape",
        });
    };

    return (
        <MoleDialog open={open} onClose={onClose} maxWidth={hasField(fields, "orientation") ? "md" : "sm"} fullWidth themeColor={themeColor}>
            <MoleDialogHeader
                title={title || t("common:dialog.filterConfig")}
                icon={<FilterListOutlined />}
                onClose={onClose}
                themeColor={themeColor}
            />
            <DialogContent sx={{ pt: 1 }}>
                <Box sx={{ mt: 1 }}>
                    {generalError && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {generalError}
                        </Alert>
                    )}

                    {shouldShowDateModeSwitch && (
                        <ToggleButtonGroup
                            color="primary"
                            value={dateMode}
                            exclusive
                            onChange={(_, mode) => {
                                if (!mode) return;
                                setDateMode(mode);
                                if (mode === "date") {
                                    setValues((prev) => ({
                                        ...prev,
                                        dateRange: {
                                            from: null,
                                            to: null,
                                        },
                                    }));
                                } else {
                                    setValues((prev) => ({
                                        ...prev,
                                        date: null,
                                    }));
                                }
                            }}
                            sx={{ mb: 2 }}
                        >
                            <ToggleButton value="date">{t("common:ui.labels.date")}</ToggleButton>
                            <ToggleButton value="range">{t("common:ui.labels.range")}</ToggleButton>
                        </ToggleButtonGroup>
                    )}

                    <Grid container spacing={2}>
                        {hasField(fields, "client") && (
                            <Grid size={{ xs: 12 }}>
                                <Autocomplete
                                    id="dynamic-client-input"
                                    size="small"
                                    options={clientOptions}
                                    filterOptions={createFilterOptions((opt) => opt.acc_name || "")}
                                    getOptionLabel={(client) => client.acc_name || ""}
                                    value={clientOptions.find((c) => c.id === values.clientId) || null}
                                    onChange={(_, newValue) => {
                                        setValues((prev) => ({ ...prev, clientId: newValue?.id || null }));
                                    }}
                                    loading={isAccountsLoading}
                                    autoHighlight
                                    autoComplete
                                    autoSelect
                                    disabled={isAccountsLoading}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={
                                                <span>
                                                    {t("common:ui.labels.client")}&nbsp;&nbsp;
                                                    <RefreshButton
                                                        isWorking={isAccountsLoading}
                                                        onClick={fetchAccounts}
                                                        disabled={isAccountsLoading}
                                                    />
                                                </span>
                                            }
                                            variant="filled"
                                            fullWidth
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        {hasField(fields, "clients") && (
                            <Grid size={{ xs: 12 }}>
                                <Autocomplete
                                    multiple
                                    id="dynamic-clients-input"
                                    size="small"
                                    options={clientOptions}
                                    filterOptions={createFilterOptions((opt) => opt.acc_name || "")}
                                    getOptionLabel={(client) => client.acc_name || ""}
                                    value={clientOptions.filter((c) => values.clientIds.includes(c.id))}
                                    onChange={(_, newValue) => {
                                        setValues((prev) => ({
                                            ...prev,
                                            clientIds: newValue.map((client) => client.id),
                                        }));
                                    }}
                                    loading={isAccountsLoading}
                                    autoHighlight
                                    disableCloseOnSelect
                                    disabled={isAccountsLoading}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={
                                                <span>
                                                    {t("common:ui.labels.clients")}&nbsp;&nbsp;
                                                    <RefreshButton
                                                        isWorking={isAccountsLoading}
                                                        onClick={fetchAccounts}
                                                        disabled={isAccountsLoading}
                                                    />
                                                </span>
                                            }
                                            variant="filled"
                                            fullWidth
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        {hasField(fields, "agent") && (
                            <Grid size={{ xs: 12 }}>
                                <Autocomplete
                                    id="dynamic-agent-input"
                                    size="small"
                                    options={agentOptions}
                                    filterOptions={createFilterOptions((opt) => opt.acc_name || "")}
                                    getOptionLabel={(agent) => agent.acc_name || ""}
                                    value={agentOptions.find((a) => a.id === values.agentId) || null}
                                    onChange={(_, newValue) => {
                                        setValues((prev) => ({ ...prev, agentId: newValue?.id || null }));
                                    }}
                                    loading={isAccountsLoading}
                                    autoHighlight
                                    autoComplete
                                    autoSelect
                                    disabled={isAccountsLoading}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={
                                                <span>
                                                    {t("common:ui.labels.agent")}&nbsp;&nbsp;
                                                    <RefreshButton
                                                        isWorking={isAccountsLoading}
                                                        onClick={fetchAccounts}
                                                        disabled={isAccountsLoading}
                                                    />
                                                </span>
                                            }
                                            variant="filled"
                                            fullWidth
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        {hasField(fields, "suppliers") && (
                            <Grid size={{ xs: 12 }}>
                                <Autocomplete
                                    multiple
                                    id="dynamic-suppliers-input"
                                    size="small"
                                    options={suppliers}
                                    filterOptions={createFilterOptions((opt) => opt.sup_name || "")}
                                    getOptionLabel={(supplier) => supplier.sup_name || ""}
                                    value={suppliers.filter((s) => values.supplierIds.includes(s.id))}
                                    onChange={(_, newValue) => {
                                        setValues((prev) => ({
                                            ...prev,
                                            supplierIds: newValue.map((supplier) => supplier.id),
                                        }));
                                    }}
                                    loading={isSuppliersLoading}
                                    autoHighlight
                                    disableCloseOnSelect
                                    disabled={isSuppliersLoading}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={
                                                <span>
                                                    {t("common:tables.supplier")}&nbsp;&nbsp;
                                                    <RefreshButton
                                                        isWorking={isSuppliersLoading}
                                                        onClick={fetchSuppliers}
                                                        disabled={isSuppliersLoading}
                                                    />
                                                </span>
                                            }
                                            variant="filled"
                                            fullWidth
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        {hasField(fields, "agents") && (
                            <Grid size={{ xs: 12 }}>
                                <Autocomplete
                                    multiple
                                    id="dynamic-agents-input"
                                    size="small"
                                    options={agentOptions}
                                    filterOptions={createFilterOptions((opt) => opt.acc_name || "")}
                                    getOptionLabel={(agent) => agent.acc_name || ""}
                                    value={agentOptions.filter((a) => values.agentIds.includes(a.id))}
                                    onChange={(_, newValue) => {
                                        setValues((prev) => ({
                                            ...prev,
                                            agentIds: newValue.map((agent) => agent.id),
                                        }));
                                    }}
                                    loading={isAccountsLoading}
                                    autoHighlight
                                    disableCloseOnSelect
                                    disabled={isAccountsLoading}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={
                                                <span>
                                                    {t("common:ui.labels.agents")}&nbsp;&nbsp;
                                                    <RefreshButton
                                                        isWorking={isAccountsLoading}
                                                        onClick={fetchAccounts}
                                                        disabled={isAccountsLoading}
                                                    />
                                                </span>
                                            }
                                            variant="filled"
                                            fullWidth
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        {hasField(fields, "depot") && (
                            <Grid size={{ xs: 12 }}>
                                <Autocomplete
                                    id="dynamic-depot-input"
                                    size="small"
                                    options={depots}
                                    filterOptions={createFilterOptions((opt) => opt.DepotName || "")}
                                    getOptionLabel={(depot) => depot.DepotName || ""}
                                    value={depots.find((d) => d.id === values.depotId) || null}
                                    onChange={(_, newValue) => {
                                        setValues((prev) => ({ ...prev, depotId: newValue?.id || null }));
                                    }}
                                    loading={isDepotsLoading}
                                    autoHighlight
                                    autoComplete
                                    autoSelect
                                    disabled={isDepotsLoading}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={
                                                <span>
                                                    {t("common:ui.labels.depot")}&nbsp;&nbsp;
                                                    <RefreshButton
                                                        isWorking={isDepotsLoading}
                                                        onClick={fetchDepots}
                                                        disabled={isDepotsLoading}
                                                    />
                                                </span>
                                            }
                                            variant="filled"
                                            fullWidth
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        {hasField(fields, "inventory-type") && (
                            <Grid size={{ xs: 12 }}>
                                <Autocomplete
                                    id="dynamic-inv-type-input"
                                    size="small"
                                    options={INVENTORY_TYPES}
                                    filterOptions={createFilterOptions((opt) => getInventoryTypeLabel(opt))}
                                    getOptionLabel={(item) => getInventoryTypeLabel(item)}
                                    value={INVENTORY_TYPES.find((item) => item.key === values.inventoryType) || null}
                                    onChange={(_, newValue) => {
                                        setValues((prev) => ({ ...prev, inventoryType: newValue?.key || null }));
                                    }}
                                    autoHighlight
                                    autoComplete
                                    autoSelect
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t("common:ui.labels.inventoryType")}
                                            variant="filled"
                                            fullWidth
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        {hasField(fields, "date") && dateMode === "date" && (
                            <Grid size={{ xs: 12 }}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label={t("common:dialog.selectDate")}
                                        value={values.date}
                                        minDate={minDate}
                                        maxDate={new Date()}
                                        onChange={(newDate) => {
                                            if (
                                                !newDate ||
                                                Util.compareStrings(newDate.toString(), "Invalid Date") ||
                                                newDate < minDate ||
                                                newDate > new Date()
                                            ) {
                                                setDateError("dateInput");
                                                return;
                                            }

                                            const date = newDate instanceof Date ? newDate : newDate.toDate();
                                            setValues((prev) => ({ ...prev, date }));
                                        }}
                                        slotProps={{
                                            textField: {
                                                id: "dateInput",
                                                size: "small",
                                                fullWidth: true,
                                                variant: "filled",
                                                error: errorInput === "dateInput",
                                                helperText:
                                                    errorInput === "dateInput"
                                                        ? t("common:validation.invalidDate")
                                                        : "",
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        )}

                        {hasField(fields, "date-range") && dateMode === "range" && (
                            <>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            label={t("common:dialog.selectStartDate")}
                                            value={values.dateRange.from}
                                            minDate={minDate}
                                            maxDate={new Date()}
                                            onChange={(newDate) => {
                                                if (
                                                    !newDate ||
                                                    Util.compareStrings(newDate.toString(), "Invalid Date") ||
                                                    newDate < minDate ||
                                                    newDate > new Date()
                                                ) {
                                                    setDateError("startDateInput");
                                                    return;
                                                }

                                                const date = newDate instanceof Date ? newDate : newDate.toDate();
                                                setValues((prev) => ({
                                                    ...prev,
                                                    dateRange: {
                                                        ...prev.dateRange,
                                                        from: date,
                                                    },
                                                }));
                                            }}
                                            slotProps={{
                                                textField: {
                                                    id: "startDateInput",
                                                    size: "small",
                                                    fullWidth: true,
                                                    variant: "filled",
                                                    error: errorInput === "startDateInput",
                                                    helperText:
                                                        errorInput === "startDateInput"
                                                            ? t("common:validation.invalidDate")
                                                            : "",
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            label={t("common:dialog.selectEndDate")}
                                            value={values.dateRange.to}
                                            minDate={minDate}
                                            maxDate={new Date()}
                                            onChange={(newDate) => {
                                                if (
                                                    !newDate ||
                                                    Util.compareStrings(newDate.toString(), "Invalid Date") ||
                                                    newDate < minDate ||
                                                    newDate > new Date()
                                                ) {
                                                    setDateError("endDateInput");
                                                    return;
                                                }

                                                const date = newDate instanceof Date ? newDate : newDate.toDate();
                                                setValues((prev) => ({
                                                    ...prev,
                                                    dateRange: {
                                                        ...prev.dateRange,
                                                        to: date,
                                                    },
                                                }));
                                            }}
                                            slotProps={{
                                                textField: {
                                                    id: "endDateInput",
                                                    size: "small",
                                                    fullWidth: true,
                                                    variant: "filled",
                                                    error: errorInput === "endDateInput",
                                                    helperText:
                                                        errorInput === "endDateInput"
                                                            ? t("common:validation.invalidDate")
                                                            : "",
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                            </>
                        )}
                    </Grid>

                    {hasField(fields, "orientation") && (
                        <Box sx={{ mt: 2.5 }}>
                            <PageOrientationCards
                                value={values.orientation}
                                onChange={(orientation) => setValues((prev) => ({ ...prev, orientation }))}
                            />
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <MoleDialogActions>
                <Button variant="outlined" color="error" onClick={resetFilters}>
                    {t("common:ui.buttons.delete")}
                </Button>
                <MoleCancelButton onClick={onClose}>{t("common:ui.buttons.cancel")}</MoleCancelButton>
                <MolePrimaryButton onClick={handleApply} startIcon={<FileDownloadOutlined />}>
                    {t("common:ui.buttons.export")}
                </MolePrimaryButton>
            </MoleDialogActions>
        </MoleDialog>
    );
};

export default DynamicReportFilterDialog;
